import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Shield, TrendingUp, Building2, Lock, Award, Cpu,
  CheckCircle2, ArrowRight, ArrowLeft, Phone, Star, Mail
} from 'lucide-react';
import { servicesService } from '../../services/api';
import { useSettings } from '../../context/SettingsContext';
import LoadingSpinner from '../../components/ui/LoadingSpinner';

const iconMap = {
  strategy: Shield,
  wealth: TrendingUp,
  banking: Building2,
  compliance: Lock,
  education: Award,
  support: Cpu,
};

function ServiceDetailPage() {
  const { slug } = useParams();
  const [service, setService] = useState(null);
  const [otherServices, setOtherServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Utiliser le context
  const { phoneNumber, email, loading: settingsLoading } = useSettings();

  useEffect(() => {
    loadService();
    loadOtherServices();
  }, [slug]);

  const loadService = async () => {
    try {
      const response = await servicesService.getBySlug(slug);
      setService(response.data.service);
    } catch (error) {
      console.error('Erreur:', error);
      setError('Service non trouvé');
    } finally {
      setLoading(false);
    }
  };

  const loadOtherServices = async () => {
    try {
      const response = await servicesService.getAll();
      const allServices = response.data.services || [];
      const filtered = allServices.filter(s => s.slug !== slug);
      setOtherServices(filtered);
    } catch (error) {
      console.error('Erreur:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" text="Chargement..." />
      </div>
    );
  }

  if (error || !service) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-dark-900 mb-4">Service non trouvé</h1>
          <Link to="/services" className="btn-primary">
            <ArrowLeft className="w-5 h-5 mr-2" />
            Retour aux services
          </Link>
        </div>
      </div>
    );
  }

  const IconComponent = iconMap[service.icon] || Shield;

  return (
    <div>
      {/* Hero */}
      <section className="relative py-16 lg:py-20 bg-gradient-to-br from-dark-900 via-primary-900 to-dark-900 overflow-hidden">
        <div className="absolute inset-0 hero-pattern opacity-10" />
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-primary-500/20 rounded-full blur-3xl" />
        
        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-3xl mx-auto"
          >
            <div className="w-20 h-20 bg-white/10 backdrop-blur rounded-2xl flex items-center justify-center mx-auto mb-6">
              <IconComponent className="w-10 h-10 text-white" />
            </div>
            
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
              {service.title}
            </h1>
            <p className="text-lg text-dark-300">
              {service.short_description}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content Section */}
      <section className="section bg-dark-50 py-16">
        <div className="container-custom">
          <div className="grid lg:grid-cols-3 gap-8">
            
            {/* Main Content - Left Side */}
            <div className="lg:col-span-2 space-y-6">
              
              {/* Card Vue d'ensemble */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="card bg-white rounded-2xl p-8 shadow-soft"
              >
                <h2 className="text-xl font-bold text-dark-900 mb-6">
                  Vue d'ensemble
                </h2>
                <div className="prose prose-lg max-w-none text-dark-600">
                  <p className="whitespace-pre-line leading-relaxed">
                    {service.description || service.short_description}
                  </p>
                </div>
              </motion.div>

              {/* Card Avantages */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="bg-dark-900 rounded-2xl p-8"
              >
                <div className="flex items-center gap-3 mb-6">
                  <Star className="w-5 h-5 text-primary-400" />
                  <h3 className="text-xl font-bold text-white">
                    Avantages
                  </h3>
                </div>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="flex items-center gap-3">
                    <CheckCircle2 className="w-4 h-4 text-secondary-400 flex-shrink-0" />
                    <p className="text-white text-sm">Expertise avérée et équipe certifiée</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <CheckCircle2 className="w-4 h-4 text-secondary-400 flex-shrink-0" />
                    <p className="text-white text-sm">Approche personnalisée selon vos besoins</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <CheckCircle2 className="w-4 h-4 text-secondary-400 flex-shrink-0" />
                    <p className="text-white text-sm">Support et accompagnement continu</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <CheckCircle2 className="w-4 h-4 text-secondary-400 flex-shrink-0" />
                    <p className="text-white text-sm">Conformité aux normes internationales</p>
                  </div>
                </div>
              </motion.div>

              {/* Card Features */}
              {service.features && service.features.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="card bg-white rounded-2xl p-8 shadow-soft"
                >
                  <h2 className="text-xl font-bold text-dark-900 mb-6">
                    Ce que nous offrons
                  </h2>
                  <div className="grid sm:grid-cols-2 gap-4">
                    {service.features.map((feature, index) => (
                      <div
                        key={index}
                        className="flex items-start gap-3 p-4 bg-dark-50 rounded-xl"
                      >
                        <CheckCircle2 className="w-5 h-5 text-secondary-500 mt-0.5 flex-shrink-0" />
                        <span className="text-dark-700 font-medium">{feature}</span>
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}
            </div>

            {/* Sidebar - Right Side */}
            <div className="lg:col-span-1">
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className="sticky top-24 space-y-6"
              >
                {/* Card Contact */}
                <div className="card bg-white rounded-2xl p-6 shadow-soft">
                  <h3 className="text-lg font-semibold text-dark-900 mb-2">
                    Besoin de ce service ?
                  </h3>
                  <p className="text-dark-500 text-sm mb-6">
                    Contactez-nous pour discuter de votre projet et obtenir un devis personnalisé.
                  </p>
                  
                  <Link to="/contact" className="btn-primary w-full mb-3">
                    <ArrowRight className="w-4 h-4 mr-2" />
                    Demande de devis
                  </Link>
                  
                  <Link to="/contact" className="btn-secondary w-full">
                    <Mail className="w-4 h-4 mr-2" />
                    Nous contacter
                  </Link>

                  {/* Contact Info - DYNAMIQUE via Context */}
                  <div className="mt-6 pt-6 border-t border-dark-100 space-y-3">
                    <div className="flex items-center gap-2 text-dark-600 text-sm">
                      <Phone className="w-4 h-4 flex-shrink-0" />
                      {phoneNumber ? (
                        <a 
                          href={`tel:${phoneNumber.replace(/\s+/g, '')}`}
                          className="hover:text-primary-600 transition-colors"
                        >
                          {phoneNumber}
                        </a>
                      ) : (
                        <span>{settingsLoading ? 'Chargement...' : 'Non disponible'}</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 text-dark-600 text-sm">
                      <Mail className="w-4 h-4 flex-shrink-0" />
                      {email ? (
                        <a 
                          href={`mailto:${email}`}
                          className="hover:text-primary-600 transition-colors"
                        >
                          {email}
                        </a>
                      ) : (
                        <span>{settingsLoading ? 'Chargement...' : 'Non disponible'}</span>
                      )}
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Section autres services */}
      {otherServices.length > 0 && (
        <section className="section bg-white py-16">
          <div className="container-custom">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-2xl md:text-3xl font-bold text-dark-900">
                Découvrez nos autres services
              </h2>
            </motion.div>

            <div className="grid md:grid-cols-3 gap-6">
              {otherServices.slice(0, 3).map((otherService) => {
                const OtherIconComponent = iconMap[otherService.icon] || Shield;
                
                return (
                  <motion.div
                    key={otherService.id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                  >
                    <Link to={`/services/${otherService.slug}`}>
                      <div className="card p-6 hover:shadow-md transition-all group bg-white border border-dark-100 rounded-xl h-full">
                        <div className="w-14 h-14 bg-primary-50 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                          <OtherIconComponent className="w-7 h-7 text-primary-600" />
                        </div>
                        <h3 className="text-lg font-semibold text-dark-900 mb-2 group-hover:text-primary-600 transition-colors">
                          {otherService.title}
                        </h3>
                        <p className="text-dark-500 text-sm line-clamp-2">
                          {otherService.short_description}
                        </p>
                      </div>
                    </Link>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}

export default ServiceDetailPage;